import React, { useState, useEffect, useMemo } from 'react';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  Fade,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  alpha,
  useTheme,
} from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import QuadrimestreSelector, { getQuadrimestreAtual, formatQuadrimestreParam } from '../../components/saude/QuadrimestreSelector';
import { buildResumoExecutivoContext } from '../../utils/saude-context-text';

const CLASSIFICACAO_COLORS: Record<string, string> = {
  Otimo: '#2e7d32',
  Bom: '#1565c0',
  Suficiente: '#e65100',
  Regular: '#c62828',
};

const formatCurrency = (value: number): string =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });

// Briefing executivo: a IA sintetiza os numeros do contexto, nunca calcula.
const BRIEFING_PROMPT = `Voce e analista de gestao em saude publica. Escreva um briefing executivo de 1 pagina para o Secretario Municipal de Saude, a partir EXCLUSIVAMENTE dos dados fornecidos no contexto. NAO invente nem recalcule numeros — use apenas os valores do contexto (score, R$, gaps, unidades e indicadores). Tom objetivo e focado em decisao.

Produza EXATAMENTE as 4 secoes abaixo, usando H3 e sendo conciso:

### Panorama do Quadrimestre
2-3 frases: score consolidado e classificacao, e o quadro geral de financiamento (R$ assegurado x potencial x em jogo, mensal e anual).

### Onde o dinheiro esta em jogo
Destaque as unidades com maior R$ em jogo e os indicadores que mais impactam o financiamento. Seja direto sobre onde estao as maiores perdas potenciais.

### Prioridades para o Quadrimestre
3-5 acoes de gestao concretas e priorizadas por retorno financeiro/impacto, ligando cada acao ao indicador ou unidade correspondente.

### Recado ao Secretario
1-2 frases de sintese executiva: a mensagem principal e a decisao mais urgente.`;

const SaudeResumoExecutivo: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [exportando, setExportando] = useState(false);
  const [quadrimestre, setQuadrimestre] = useState(getQuadrimestreAtual().quadrimestre);
  const [ano, setAno] = useState(getQuadrimestreAtual().ano);

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    const qParam = formatQuadrimestreParam(quadrimestre, ano);
    saudeService.getResumoExecutivo(municipioAtivo.id, qParam, unidadeSelecionada?.codunidade)
      .then((res: any) => setData(res))
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar o resumo executivo.'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, quadrimestre, ano, unidadeSelecionada?.codunidade]);

  const dadosContexto = useMemo(() => {
    if (!data || data.sem_dados) return undefined;
    return buildResumoExecutivoContext(data);
  }, [data]);

  const handleExportPdf = () => {
    if (!municipioAtivo?.id) return;
    setExportando(true);
    const qParam = formatQuadrimestreParam(quadrimestre, ano);
    saudeService.exportarResumoExecutivoPdf(municipioAtivo.id, qParam, unidadeSelecionada?.codunidade)
      .catch(() => setError('Falha ao exportar PDF.'))
      .finally(() => setExportando(false));
  };

  const aproveitamento = data?.financeiro?.receitaMaxima > 0
    ? Math.round((data.financeiro.receitaMensal / data.financeiro.receitaMaxima) * 100)
    : null;

  if (!municipioAtivo?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Selecione um municipio para acessar o resumo executivo.</Alert>
      </Box>
    );
  }

  return (
    <>
      <Fade in>
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                <SummarizeIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#7c4dff' }} />
                Resumo Executivo do Quadrimestre
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Briefing de gestao: score consolidado, financiamento em jogo e prioridades
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportPdf}
                disabled={loading || exportando || !data || data.sem_dados}
              >
                Exportar PDF
              </Button>
              <QuadrimestreSelector
                quadrimestre={quadrimestre}
                ano={ano}
                onChange={(q, a) => { setQuadrimestre(q); setAno(a); }}
              />
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map(i => (
                <Grid item xs={12} sm={6} md={3} key={i}><Skeleton variant="rounded" height={130} /></Grid>
              ))}
            </Grid>
          ) : data && data.sem_dados ? (
            <Alert severity="info">
              Nenhum dado disponivel para este quadrimestre. Verifique a conexao e-SUS do municipio ou selecione outro periodo.
            </Alert>
          ) : data && (
            <>
              {/* Cards de score + R$ */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Score Consolidado</Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: CLASSIFICACAO_COLORS[data.classificacao_geral] || '#455a64' }}>
                        {data.score_geral}
                      </Typography>
                      <Chip
                        label={data.classificacao_geral}
                        size="small"
                        sx={{
                          mt: 0.5,
                          backgroundColor: alpha(CLASSIFICACAO_COLORS[data.classificacao_geral] || '#455a64', 0.1),
                          color: CLASSIFICACAO_COLORS[data.classificacao_geral] || '#455a64',
                          fontWeight: 600,
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Receita Assegurada</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#1565c0' }}>
                        {formatCurrency(data.financeiro.receitaMensal)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(data.financeiro.receitaAnual)}/ano
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Receita Potencial</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#2e7d32' }}>
                        {formatCurrency(data.financeiro.receitaMaxima)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {aproveitamento != null ? `${aproveitamento}% aproveitado` : `${formatCurrency(data.financeiro.receitaMaximaAnual)}/ano`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Em Jogo (gap)</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#c62828' }}>
                        {formatCurrency(data.financeiro.gap)}
                      </Typography>
                      <Chip
                        size="small"
                        icon={<WarningAmberIcon />}
                        label={`${formatCurrency(data.financeiro.gapAnual)}/ano`}
                        color="error"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Top 3 unidades + Top 3 indicadores */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                        <AttachMoneyIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: 20, color: '#c62828' }} />
                        Unidades em risco (eSF/eAP)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        R$/mes em jogo estimado (rateio do gap eSF/eAP por desempenho)
                      </Typography>
                      {data.top_unidades_risco?.length ? (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Unidade</TableCell>
                                <TableCell align="center">Equipes</TableCell>
                                <TableCell align="center">Classif.</TableCell>
                                <TableCell align="right">R$/mes estim.</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {data.top_unidades_risco.map((u: any) => (
                                <TableRow key={u.nome} hover>
                                  <TableCell>{u.nome}</TableCell>
                                  <TableCell align="center">{u.equipes}</TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={u.classificacao}
                                      size="small"
                                      sx={{
                                        backgroundColor: alpha(CLASSIFICACAO_COLORS[u.classificacao] || '#9e9e9e', 0.1),
                                        color: CLASSIFICACAO_COLORS[u.classificacao] || '#9e9e9e',
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, color: '#c62828' }}>{formatCurrency(u.r_estimado)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {unidadeSelecionada ? 'Ranking indisponivel com uma unidade filtrada.' : 'Sem unidades eSF/eAP com gap.'}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                        <WarningAmberIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: 20, color: '#e65100' }} />
                        Indicadores que mais impactam o financiamento
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        R$ estimado recuperavel via melhoria do indicador
                      </Typography>
                      {data.top_indicadores_risco?.length ? (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Indicador</TableCell>
                                <TableCell align="center">Classif.</TableCell>
                                <TableCell align="right">R$/mes estim.</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {data.top_indicadores_risco.map((ind: any) => (
                                <TableRow key={ind.eixo} hover>
                                  <TableCell>{ind.nome}</TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={ind.classificacao}
                                      size="small"
                                      sx={{
                                        backgroundColor: alpha(CLASSIFICACAO_COLORS[ind.classificacao] || '#9e9e9e', 0.1),
                                        color: CLASSIFICACAO_COLORS[ind.classificacao] || '#9e9e9e',
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, color: '#e65100' }}>{formatCurrency(ind.r_estimado)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Todos os indicadores no maximo (sem gap).</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Fade>
      {municipioAtivo && dadosContexto && (
        <AnalisePredicaoCard
          dadosContexto={dadosContexto}
          municipioId={municipioAtivo.id}
          painelId="resumo-executivo"
          quadrimestre={quadrimestre}
          ano={ano}
          titulo="Briefing Executivo IA"
          subtitulo="Panorama, onde o dinheiro esta em jogo e prioridades"
          prompt={BRIEFING_PROMPT}
          manualTrigger
        />
      )}
      <ChatIAButton contexto="saude" dadosContexto={dadosContexto} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </>
  );
};

export default SaudeResumoExecutivo;
